import React, { useState, useEffect } from 'react';
import './OrgPolicies.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

const OrgPolicies = () => {
  const [enforcementMode, setEnforcementMode] = useState('optional');
  const [jitEnabled, setJitEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [currentOrgId, setCurrentOrgId] = useState('');
  const [currentOrgName, setCurrentOrgName] = useState('');
  const [hasSamlConnection, setHasSamlConnection] = useState(false);
  const [checkingSaml, setCheckingSaml] = useState(true);

  useEffect(() => {
    const loadOrgPolicy = () => {
      try {
        setLoading(true);
        const societyId = localStorage.getItem('societyId');
        const societiesData = localStorage.getItem('societies');
        
        if (!societyId || !societiesData) {
          setError('No organization selected');
          return;
        }

        const societies = JSON.parse(societiesData);
        const currentSociety = societies.find(org => org.OrgId === societyId);
        
        if (!currentSociety) {
          setError('Organization not found');
          return;
        }

        setCurrentOrgId(currentSociety.OrgId);
        setCurrentOrgName(currentSociety.Name);

        // Read MFA policy from organization's Policies object
        if (currentSociety.Policies && 
            currentSociety.Policies.MFAPolicy && 
            currentSociety.Policies.MFAPolicy.EnforcementMode) {
          setEnforcementMode(currentSociety.Policies.MFAPolicy.EnforcementMode);
        } else {
          // Default to optional if not set
          setEnforcementMode('optional');
        }

        // Read JIT policy from organization's Policies object
        if (currentSociety.Policies && 
            currentSociety.Policies.JITPolicy && 
            currentSociety.Policies.JITPolicy.Enabled === true) {
          setJitEnabled(true);
        } else {
          setJitEnabled(false);
        }
      } catch (error) {
        console.error('Error loading organization policy:', error);
        setError('Failed to load organization data');
      } finally {
        setLoading(false);
      }
    };

    loadOrgPolicy();
    checkSamlConnections();
  }, []);

  const checkSamlConnections = async () => {
    try {
      setCheckingSaml(true);
      const token = localStorage.getItem('accessToken');
      const societyId = localStorage.getItem('societyId');

      if (!token || !societyId) {
        setHasSamlConnection(false);
        return;
      }

      const response = await fetch(
        `${API_URL}/api/v1/auth/orgs/${societyId}/connections?access_token=${encodeURIComponent(token)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        const connections = data.data?.Data || [];
        // Check if there's at least one active SAML connection
        const activeSamlConnection = connections.some(
          conn => conn.ConnectionType === 'saml_custom' && conn.IsActive
        );
        setHasSamlConnection(activeSamlConnection);
      } else {
        setHasSamlConnection(false);
      }
    } catch (error) {
      console.error('Error checking SAML connections:', error);
      setHasSamlConnection(false);
    } finally {
      setCheckingSaml(false);
    }
  };

  const handleSave = async () => {
    if (!currentOrgId) {
      setError('No organization selected');
      return;
    }

    // Check if trying to enable JIT without SAML connection
    if (jitEnabled && !hasSamlConnection) {
      setError('Cannot enable JIT provisioning without an active SAML connection. Please add a SAML connection first.');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess('');

    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        setError('No access token found. Please log in again.');
        return;
      }

      const payload = {
        MFAPolicy: {
          EnforcementMode: enforcementMode
        },
        JITPolicy: {
          Enabled: jitEnabled
        }
      };

      const response = await fetch(
        `${API_URL}/api/v1/auth/orgs/${currentOrgId}/policies?access_token=${encodeURIComponent(token)}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        }
      );

      if (response.ok) {
        setSuccess('Policies updated successfully!');
        
        // Update localStorage with new policies
        const societiesData = localStorage.getItem('societies');
        if (societiesData) {
          const societies = JSON.parse(societiesData);
          const updatedSocieties = societies.map(org => {
            if (org.OrgId === currentOrgId) {
              return {
                ...org,
                Policies: {
                  ...org.Policies,
                  MFAPolicy: {
                    EnforcementMode: enforcementMode
                  },
                  JITPolicy: {
                    Enabled: jitEnabled
                  }
                }
              };
            }
            return org;
          });
          localStorage.setItem('societies', JSON.stringify(updatedSocieties));
        }
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setSuccess('');
        }, 3000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to update policy');
      }
    } catch (error) {
      console.error('Error updating policy:', error);
      setError(error.message || 'Failed to update policies');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="policies-loading">Loading policies...</div>;
  }

  if (error && !currentOrgId) {
    return <div className="policies-error">{error}</div>;
  }

  return (
    <div className="policies-container">
      <h2>Organization Policies</h2>
      <p className="org-subtitle">{currentOrgName}</p>

      {success && (
        <div className="success-message">
          {success}
        </div>
      )}

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="policy-card">
        <div className="policy-card-header">
          <h3>Multi-Factor Authentication (MFA)</h3>
          <p>Configure how MFA is enforced for users in your organization</p>
        </div>

        <div className="policy-options">
          <label className="policy-option">
            <input
              type="radio"
              name="enforcementMode"
              value="optional"
              checked={enforcementMode === 'optional'}
              onChange={(e) => setEnforcementMode(e.target.value)}
              disabled={saving}
            />
            <div className="option-details">
              <strong>Optional</strong>
              <span>Users can choose whether to enable MFA</span>
            </div>
          </label>

          <label className="policy-option">
            <input
              type="radio"
              name="enforcementMode"
              value="force"
              checked={enforcementMode === 'force'}
              onChange={(e) => setEnforcementMode(e.target.value)}
              disabled={saving}
            />
            <div className="option-details">
              <strong>Required</strong>
              <span>All users must enable MFA to access their accounts</span>
            </div>
          </label>
        </div>
      </div>

      <div className="policy-card">
        <div className="policy-card-header">
          <h3>Just-in-Time (JIT) Provisioning</h3>
          <p>Automatically create user accounts during SSO authentication</p>
        </div>

        {!hasSamlConnection && !checkingSaml && (
          <div className="warning-message">
            <i className="fas fa-exclamation-triangle"></i>
            <span>
              <strong>SAML Connection Required:</strong> You need to configure an active SAML connection before enabling JIT provisioning. 
              Please add a SAML connection in the <strong>SAML Connections</strong> tab first.
            </span>
          </div>
        )}

        <div className="policy-options">
          <label className={`policy-option toggle-option ${!hasSamlConnection && !jitEnabled ? 'disabled-option' : ''}`}>
            <input
              type="checkbox"
              checked={jitEnabled}
              onChange={(e) => {
                // Only allow enabling if SAML connection exists
                if (e.target.checked && !hasSamlConnection) {
                  setError('Cannot enable JIT provisioning without an active SAML connection.');
                  return;
                }
                setJitEnabled(e.target.checked);
              }}
              disabled={saving || (!hasSamlConnection && !jitEnabled)}
              className="toggle-checkbox"
            />
            <div className="option-details">
              <strong>Enable JIT Provisioning</strong>
              <span>Automatically provision users on first login via SSO</span>
            </div>
          </label>
        </div>
      </div>

      <button
        className="save-button"
        onClick={handleSave}
        disabled={saving}
      >
        {saving ? 'Saving...' : 'Save All Changes'}
      </button>

      <div className="info-note">
        <strong>Note:</strong> Changes to policies will apply to all users in the organization.
      </div>
    </div>
  );
};

export default OrgPolicies;

